import { Field, Input, Switch, Textarea } from '@fluentui/react-components'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Button } from '../Button'
import './Form.scss'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useQueryClient } from '@tanstack/react-query'

export type OrganisationForm = {
  name: string
  longName: string
  description: string
  active: boolean
  domain: string
}

type MessagesSubscriptionsOrganisationSummary = RestSchemas['Messages.Subscriptions.OrganisationSummary']

type AddOrganisationFormProps = {
  onSubmit: () => void
  // edit?: boolean
  selectedOrg?: MessagesSubscriptionsOrganisationSummary
}

export const AddOrganisationForm = (props: AddOrganisationFormProps) => {
  const edit = !!props.selectedOrg
  const queryClient = useQueryClient()
  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<OrganisationForm>({
    defaultValues: {
      name: props.selectedOrg?.name ?? '',
      longName: props.selectedOrg?.longName ?? '',
      description: props.selectedOrg?.description ?? '',
      domain: props.selectedOrg?.whitelistedDomain ?? '',
      active: props.selectedOrg?.active != null ? props.selectedOrg.active : true,
    },
  })

  const {
    getSubscriptionsApiGetOrganisations,
    postSubscriptionsApiUpdateOrganisation,
    putSubscriptionsApiAddOrganisation,
  } = createRestClient()

  const { mutate: updateOrganisation } = postSubscriptionsApiUpdateOrganisation.useMutation(undefined, {
    onSuccess: () => {
      getSubscriptionsApiGetOrganisations.invalidateQueries({}, queryClient)
      props.onSubmit()
      toast.success('Organisation successfully updated')
      reset()
    },
  })
  const { mutate: addOrganisation } = putSubscriptionsApiAddOrganisation.useMutation(undefined, {
    onSuccess: () => {
      getSubscriptionsApiGetOrganisations.invalidateQueries({}, queryClient)
      props.onSubmit()
      toast.success('Organisation successfully added')
      reset()
    },
  })

  const onSubmit = (data: OrganisationForm) => {
    if (edit && props.selectedOrg) {
      updateOrganisation({
        body: {
          id: props.selectedOrg.id,
          name: data.name,
          longName: data.longName,
          description: data.description,
          whitelistedDomain: data.domain,
          active: data.active,
        },
      })
    } else {
      addOrganisation({
        body: {
          name: data.name,
          longName: data.longName,
          description: data.description,
          whitelistedDomain: data.domain,
        },
      })
    }
  }

  return (
    <div className="form-wrapper">
      <div>
        <Field label="Name" validationState={errors.name ? 'error' : 'none'} validationMessage={errors.name?.message}>
          <Input
            {...register('name', {
              required: 'Name is required',
              maxLength: 100,
            })}
          />
        </Field>

        <Field
          label="Long name"
          validationState={errors.longName ? 'error' : 'none'}
          validationMessage={errors.longName?.message}
        >
          <Input {...register('longName', { maxLength: 128 })} />
        </Field>

        <Field
          label="Description"
          validationState={errors.description ? 'error' : 'none'}
          validationMessage={errors.description?.type === 'maxLength' ? 'Max length exceeded' : undefined}
        >
          <Textarea {...register('description', { maxLength: 256 })} rows={4} />
        </Field>

        <Field
          label="Active"
          validationState={errors.active ? 'error' : 'none'}
          validationMessage={errors.active?.message}
        >
          <Switch {...register('active')} />
        </Field>

        <Field
          label="Domain"
          validationState={errors.domain ? 'error' : 'none'}
          validationMessage={errors.domain?.message}
        >
          <Input {...register('domain', { maxLength: 128 })} />
        </Field>
      </div>
      <div className="form-action-container">
        <Button text="Submit" onClick={handleSubmit(onSubmit)} />
      </div>
    </div>
  )
}
